import { useEffect, useId, useState } from 'react';
import { api } from '../../api';
import { TURKISH_CITIES } from '../../data/turkishCities';

function Field({ label, children, hint, variant = 'line' }) {
  return (
    <div className={`account-change-field account-kyc-field account-kyc-field--${variant}`}>
      <div className="account-change-field__label">{label}</div>
      {hint ? <p className="account-kyc-form__hint">{hint}</p> : null}
      {variant === 'line' ? (
        <div className="account-change-field__input-wrap">
          <div className="account-change-field__line">
            <div className="account-change-field__line-content">
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div className={`account-kyc-field__box account-kyc-field__box--${variant}`}>
          {children}
        </div>
      )}
    </div>
  );
}

function UploadField({ label, hint, file, onChange, disabled, inputId }) {
  return (
    <Field label={label} hint={hint} variant="upload">
      <label className="account-kyc-upload" htmlFor={inputId}>
        <span className="account-kyc-upload__action">
          {file ? 'Değiştir' : 'Dosya seç'}
        </span>
        <span className="account-kyc-upload__name">
          {file ? file.name : 'JPG, PNG veya WEBP'}
        </span>
      </label>
      <input
        id={inputId}
        className="account-kyc-form__file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/*"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        disabled={disabled}
      />
    </Field>
  );
}

export default function ProfileKycForm({
  title,
  summary,
  token,
  onDone,
  onCancel,
  t,
}) {
  const frontId = useId();
  const backId = useId();
  const [tcKimlik, setTcKimlik] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setTcKimlik(summary?.tc_kimlik ? String(summary.tc_kimlik) : '');
    setCity(summary?.city ? String(summary.city) : '');
    setAddress(summary?.address ? String(summary.address) : '');
    setIdFront(null);
    setIdBack(null);
    setErr('');
    setMsg('');
  }, [summary?.tc_kimlik, summary?.city, summary?.address, summary?.id]);

  const status = summary?.verification_status || 'unverified';
  const pending = status === 'pending' && Boolean(summary?.kyc_submitted_at);
  const verified = status === 'verified';

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    const tc = String(tcKimlik || '').replace(/\D/g, '');
    if (!/^[1-9]\d{10}$/.test(tc)) {
      setErr(t('account.profile.kyc.tcInvalid'));
      return;
    }
    if (!TURKISH_CITIES.includes(String(city || '').trim())) {
      setErr(t('account.profile.kyc.cityRequired'));
      return;
    }
    if (String(address || '').trim().length < 5) {
      setErr(t('account.profile.kyc.addressRequired'));
      return;
    }
    if (!idFront) {
      setErr(t('account.profile.kyc.photoRequired'));
      return;
    }
    if (!idBack) {
      setErr(t('account.profile.kyc.backRequired'));
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      form.append('tcKimlik', tc);
      form.append('city', String(city).trim());
      form.append('address', String(address).trim());
      form.append('idFront', idFront);
      form.append('idBack', idBack);
      await api.submitVerification(token, form);
      setMsg(t('account.profile.kyc.submitted'));
      onDone?.();
    } catch (error) {
      setErr(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (verified) {
    return (
      <div className="account-kyc-form account-kyc-form--status">
        <h2 className="account-change-form__heading">{title}</h2>
        <p className="account-change-form__message">{t('account.profile.kyc.alreadyVerified')}</p>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="account-kyc-form account-kyc-form--status">
        <h2 className="account-change-form__heading">{title}</h2>
        <p className="account-change-form__message">{t('account.profile.kyc.pendingMessage')}</p>
        <div className="account-change-form__success">{t('account.profile.verification.pending')}</div>
        {onCancel ? (
          <div className="account-change-form__buttons account-kyc-form__buttons">
            <button type="button" className="account-change-form__submit" onClick={onCancel}>
              {t('auth.verify.cancel')}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <form className="account-change-form account-kyc-form" onSubmit={submit} noValidate>
      <div className="account-change-form__content account-kyc-form__content">
        <div className="account-change-form__controls account-kyc-form__controls">
          <h2 className="account-change-form__heading">{title}</h2>
          <p className="account-change-form__message">{t('account.profile.kyc.intro')}</p>

          <Field label={t('account.profile.kyc.tcLabel')}>
            <input
              className="account-change-field__input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={11}
              value={tcKimlik}
              onChange={(e) => setTcKimlik(e.target.value.replace(/\D/g, '').slice(0, 11))}
              disabled={loading}
            />
          </Field>

          <Field label={t('account.profile.kyc.cityLabel')}>
            <select
              className="account-change-field__input account-kyc-form__select"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={loading}
            >
              <option value="">{t('account.profile.kyc.cityPlaceholder')}</option>
              {TURKISH_CITIES.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </Field>

          <Field label={t('account.profile.kyc.addressLabel')} hint={t('account.profile.kyc.addressHint')} variant="area">
            <textarea
              className="account-change-field__input account-kyc-form__textarea"
              rows={3}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={loading}
            />
          </Field>

          <div className="account-kyc-form__uploads">
            <UploadField
              label={t('account.profile.kyc.frontLabel')}
              hint={t('account.profile.kyc.frontHint')}
              file={idFront}
              onChange={setIdFront}
              disabled={loading}
              inputId={frontId}
            />
            <UploadField
              label={t('account.profile.kyc.backLabel')}
              hint={t('account.profile.kyc.backHint')}
              file={idBack}
              onChange={setIdBack}
              disabled={loading}
              inputId={backId}
            />
          </div>

          {err ? <div className="account-change-form__error">{err}</div> : null}
          {msg ? <div className="account-change-form__success">{msg}</div> : null}

          <div className="account-change-form__buttons account-kyc-form__buttons">
            <button type="submit" className="account-change-form__submit" disabled={loading}>
              {loading ? '...' : t('account.profile.kyc.submit')}
            </button>
            {onCancel ? (
              <button type="button" className="account-change-form__submit account-kyc-form__cancel" disabled={loading} onClick={onCancel}>
                {t('auth.verify.cancel')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </form>
  );
}
